/**
 * Master-edited overrides for every chat mode (CHAT_MODES in config/chatModes.ts).
 *
 * The defaults stay in code: config/agentProfiles.ts registers every profile,
 * five of them from config/nativeAdvisors.ts. A new mode is born in
 * config/chatModes.ts, which makes AGENT_PROFILES require its profile, and in a
 * migration that adds it to the agent_profiles CHECK and to set_agent_profile
 * (127 did so for overview and channels). This module supplies the OVERRIDE: if
 * a row exists for a mode, its text replaces the built-in wording; if not, or if
 * anything about the read fails, the built-in is used unchanged.
 *
 * The fallback is the point. A profile is what the assistant is told to be, so
 * "no profile" cannot be an outcome — the chat has to keep working through a
 * failed read, a half-saved row or an empty table. Every path here ends at a
 * usable profile.
 *
 * Only text is overridable. `availableSections` and `showReportButton` stay in
 * code because they are wiring, not authorship: a section id that no page
 * renders would give the assistant a link that goes nowhere. `defaultFollowUps`
 * stay in code too (the table has no column for them), and so do `pageRules`,
 * the page's data-reading rules: composedTips appends them after the tips on
 * every call, so a saved card can never freeze or drop them.
 *
 * @module services/agentProfileService
 */

import { getSupabaseClient, isSupabaseConfigured } from "@/config";
import { toJson } from "./dbBoundary";
import { getAgentProfile, type AgentProfile } from "@/config/agentProfiles";
import { CHAT_MODES, type ChatMode } from "@/config/chatModes";
import type { GuidedQuestion } from "./mediaChatService";
import { logError } from "./logger";

export type AgentMode = ChatMode;

/** Must equal CHAT_MODES: isMode drops a stored row whose mode is missing here, without an error. */
export const AGENT_MODES: readonly AgentMode[] = CHAT_MODES;

/**
 * The overridable half of a profile, as stored. Since 2026-10-20 the Skills page
 * edits only roleStatement and analysisTips; title, subtitle and guidedQuestions
 * are still stored and sent on save, but no page reads them.
 */
export interface AgentProfileOverride {
  mode: AgentMode;
  title: string;
  subtitle: string;
  roleStatement: string;
  analysisTips: string;
  guidedQuestions: GuidedQuestion[];
  updatedAt: string | null;
}

/**
 * Length ceilings, mirroring the CHECK constraints so the UI can say no first.
 *
 * Only roleStatement and analysisTips are counted on screen. The title, subtitle
 * and guidedQuestions limits mirror the CHECKs and the RPC (migrations 066 and
 * 108); no input on the Skills page has used them since 2026-10-20.
 *
 * Sized from the shipped profiles, not from taste: the performance advisor's
 * analysis tips are 40,400 characters — a benchmark table rather than a
 * paragraph — so a ceiling that felt generous at 4,000 would have refused the
 * product's own default text.
 */
export const PROFILE_LIMITS = {
  title: 80,
  subtitle: 160,
  roleStatement: 4000,
  analysisTips: 60000,
  guidedQuestions: 12,
} as const;

interface ProfileRow {
  mode: string;
  title: string;
  subtitle: string;
  role_statement: string;
  analysis_tips: string;
  guided_questions: unknown;
  updated_at: string | null;
}

const isMode = (value: unknown): value is AgentMode =>
  typeof value === "string" && (AGENT_MODES as readonly string[]).includes(value);

/**
 * Guided questions, or nothing.
 *
 * `text` is both the button's label and the message it sends, so an entry
 * without one is a button the reader cannot read AND a message that spends a
 * credit on nothing. Those are dropped rather than rendered. `id` only has to
 * be unique among siblings — it is the React key — so a missing one is filled
 * in by position rather than costing the author an entry.
 */
const toGuidedQuestions = (raw: unknown): GuidedQuestion[] => {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item, index) => {
    if (!item || typeof item !== "object") return [];
    const q = item as Record<string, unknown>;
    const text = typeof q.text === "string" ? q.text.trim() : "";
    if (!text) return [];
    const id = typeof q.id === "string" && q.id.trim() ? q.id.trim() : `q${index + 1}`;
    const emoji = typeof q.emoji === "string" && q.emoji ? q.emoji : "💬";
    return [{ id, emoji, text }];
  });
};

const toOverride = (row: ProfileRow): AgentProfileOverride | null => {
  if (!isMode(row.mode)) return null;
  return {
    mode: row.mode,
    title: row.title,
    subtitle: row.subtitle,
    roleStatement: row.role_statement,
    analysisTips: row.analysis_tips,
    guidedQuestions: toGuidedQuestions(row.guided_questions),
    updatedAt: row.updated_at,
  };
};

/**
 * Every stored override, keyed by mode. Empty when none are stored.
 *
 * Returns a Map rather than throwing on failure: this feeds the chat, and a
 * refused read must leave the assistant on its built-in wording rather than
 * leave it with none. That is the opposite of the rule this codebase applies to
 * METRICS — there a failed read must never look like a measurement — because a
 * missing profile has a correct answer to fall back on and a missing number
 * does not.
 */
export const fetchAgentProfileOverrides = async (): Promise<Map<AgentMode, AgentProfileOverride>> => {
  const out = new Map<AgentMode, AgentProfileOverride>();
  if (!isSupabaseConfigured()) return out;

  try {
    // Raced against a deadline. This read sits in front of every chat turn, and
    // a request that never settles would hold the turn open forever — the one
    // way this module could fail to end at a usable profile. Losing the race
    // costs the master's wording for that turn; not having one costs the turn.
    const read = getSupabaseClient()
      .from("agent_profiles")
      .select("mode, title, subtitle, role_statement, analysis_tips, guided_questions, updated_at");

    const settled = await Promise.race([
      read,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), READ_DEADLINE_MS)),
    ]);

    if (settled === null) {
      logError("AgentProfile", "Agent profile read timed out; using built-ins", null);
      return out;
    }

    const { data, error } = settled;

    if (error) {
      logError("AgentProfile", "Could not read agent profiles; using built-ins", error);
      return out;
    }

    for (const row of (data ?? []) as ProfileRow[]) {
      const override = toOverride(row);
      if (override) out.set(override.mode, override);
    }
  } catch (err) {
    logError("AgentProfile", "Could not read agent profiles; using built-ins", err);
  }
  return out;
};

/**
 * The built-in profile for a mode with any stored text laid over it.
 *
 * A blank stored field falls through to the built-in rather than blanking the
 * assistant's identity, and stored guided questions replace the built-in list
 * only when there is at least one — an empty list means "not edited", not
 * "offer nothing".
 */
export const applyOverride = (
  mode: AgentMode,
  override: AgentProfileOverride | undefined,
): AgentProfile => {
  const base = getAgentProfile(mode);
  if (!override) return base;
  return {
    ...base,
    title: override.title.trim() || base.title,
    subtitle: override.subtitle.trim() || base.subtitle,
    roleStatement: override.roleStatement.trim() || base.roleStatement,
    analysisTips: override.analysisTips.trim() || base.analysisTips,
    guidedQuestions: override.guidedQuestions.length ? override.guidedQuestions : base.guidedQuestions,
  };
};

/**
 * Overrides are cached for a minute.
 *
 * Every chat turn needs a profile, and without this each one would open the
 * turn with a round-trip before the model is even called. A minute is short
 * enough that a master watching their own edit sees it on the next question,
 * and the admin screen clears the cache on save so they do not have to wait
 * even that long.
 */
const CACHE_TTL_MS = 60_000;

/** How long a chat turn will wait for the master's wording before going without. */
const READ_DEADLINE_MS = 2_500;
let cache: { at: number; overrides: Map<AgentMode, AgentProfileOverride> } | null = null;

/** Drops the cache so the next chat turn re-reads. Called after an edit. */
export const clearAgentProfileCache = (): void => {
  cache = null;
};

const cachedOverrides = async (): Promise<Map<AgentMode, AgentProfileOverride>> => {
  if (cache && Date.now() - cache.at < CACHE_TTL_MS) return cache.overrides;
  const overrides = await fetchAgentProfileOverrides();
  cache = { at: Date.now(), overrides };
  return overrides;
};

/** The profile a chat in this mode should run on, built-in unless overridden. */
export const fetchEffectiveAgentProfile = async (mode: AgentMode): Promise<AgentProfile> =>
  applyOverride(mode, (await cachedOverrides()).get(mode));

export interface ProfileWriteResult {
  ok: boolean;
  error?: string;
}

/** Master-only; the database refuses anyone else, whatever the UI allows. */
export const saveAgentProfile = async (
  profile: Omit<AgentProfileOverride, "updatedAt">,
): Promise<ProfileWriteResult> => {
  const { error } = await getSupabaseClient().rpc("set_agent_profile", {
    p_mode: profile.mode,
    p_title: profile.title,
    p_subtitle: profile.subtitle,
    p_role_statement: profile.roleStatement,
    p_analysis_tips: profile.analysisTips,
    p_guided_questions: toJson(profile.guidedQuestions),
  });

  if (error) {
    logError("AgentProfile", "Could not save agent profile", error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
};

/** Drops the stored row so the mode returns to the wording shipped in code. */
export const resetAgentProfile = async (mode: AgentMode): Promise<ProfileWriteResult> => {
  const { error } = await getSupabaseClient().rpc("reset_agent_profile", { p_mode: mode });
  if (error) {
    logError("AgentProfile", "Could not reset agent profile", error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
};
